import React from 'react';
import { 
  GitCompare, XCircle, Trophy, Info
} from 'lucide-react';

export const ScenarioComparisonResults = ({ comparisonData, onClose }) => {
  if (!comparisonData) return null;
  
  const { scenario_a, scenario_b, differences, winner } = comparisonData;
  
  // Safe access with defaults
  const fsA = scenario_a?.financial_summary || {};
  const fsB = scenario_b?.financial_summary || {};
  const diff = differences || {};
  
  // Helper to safely get numeric values with defaults
  const safeNum = (val, defaultVal = 0) => (val !== undefined && val !== null ? val : defaultVal);
  
  const formatDiff = (value, isPercent = false, isCurrency = false) => {
    const safeValue = safeNum(value);
    if (isCurrency) {
      const prefix = safeValue >= 0 ? '+$' : '-$';
      return `${prefix}${Math.abs(safeValue).toLocaleString()}`;
    }
    if (isPercent) {
      return `${safeValue >= 0 ? '+' : ''}${safeValue.toFixed(1)}%`;
    }
    return `${safeValue >= 0 ? '+' : ''}${safeValue.toFixed(1)}`;
  };
  
  const formatValue = (value, decimals = 1) => {
    const safeValue = safeNum(value);
    return safeValue.toFixed(decimals);
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-t-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <GitCompare size={28} />
              <div>
                <h3 className="text-xl font-bold">Scenario Comparison Results</h3>
                <p className="text-blue-100">Side-by-side analysis of your two scenarios</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg">
              <XCircle size={24} />
            </button>
          </div>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Winner Banner */}
          {winner && winner !== 'tie' && (
            <div className={`p-4 rounded-lg flex items-center gap-3 ${
              winner === 'scenario_a' ? 'bg-blue-50 border-2 border-blue-200' : 'bg-purple-50 border-2 border-purple-200'
            }`}>
              <Trophy className={winner === 'scenario_a' ? 'text-blue-600' : 'text-purple-600'} size={24} />
              <div>
                <span className="font-bold text-gray-900">
                  {winner === 'scenario_a' ? 'Scenario A' : 'Scenario B'} delivers better ROI
                </span>
                <span className="text-gray-600 ml-2">
                  ({formatDiff(Math.abs(safeNum(diff.roi_diff)), true)} difference)
                </span>
              </div>
            </div>
          )}
          
          {/* Comparison Table */}
          <div className="overflow-hidden rounded-xl border">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Metric</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-blue-600 bg-blue-50">Scenario A</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-purple-600 bg-purple-50">Scenario B</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600">Difference</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                <tr>
                  <td className="px-4 py-3 font-medium text-gray-900">Total Investment</td>
                  <td className="px-4 py-3 text-center text-blue-600 bg-blue-50/50">${safeNum(fsA.total_investment).toLocaleString()}</td>
                  <td className="px-4 py-3 text-center text-purple-600 bg-purple-50/50">${safeNum(fsB.total_investment).toLocaleString()}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={safeNum(diff.investment_diff) >= 0 ? 'text-rose-600' : 'text-emerald-600'}>
                      {formatDiff(diff.investment_diff, false, true)}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium text-gray-900">Expected Impact</td>
                  <td className="px-4 py-3 text-center text-blue-600 bg-blue-50/50">${safeNum(fsA.total_financial_impact).toLocaleString()}</td>
                  <td className="px-4 py-3 text-center text-purple-600 bg-purple-50/50">${safeNum(fsB.total_financial_impact).toLocaleString()}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={safeNum(fsB.total_financial_impact) - safeNum(fsA.total_financial_impact) >= 0 ? 'text-emerald-600' : 'text-rose-600'}>
                      {formatDiff(safeNum(fsB.total_financial_impact) - safeNum(fsA.total_financial_impact), false, true)}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium text-gray-900">Net Benefit</td>
                  <td className={`px-4 py-3 text-center bg-blue-50/50 ${safeNum(fsA.net_benefit) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    ${safeNum(fsA.net_benefit).toLocaleString()}
                  </td>
                  <td className={`px-4 py-3 text-center bg-purple-50/50 ${safeNum(fsB.net_benefit) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    ${safeNum(fsB.net_benefit).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={safeNum(diff.net_benefit_diff) >= 0 ? 'text-emerald-600' : 'text-rose-600'}>
                      {formatDiff(diff.net_benefit_diff, false, true)}
                    </span>
                  </td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-4 py-3 font-bold text-gray-900 flex items-center gap-2">
                    ROI
                    {winner && winner !== 'tie' && <Trophy className="text-yellow-500" size={16} />}
                  </td>
                  <td className={`px-4 py-3 text-center font-bold ${safeNum(fsA.roi_percent) >= 0 ? 'text-emerald-600' : 'text-rose-600'} ${winner === 'scenario_a' ? 'bg-blue-100' : 'bg-blue-50/50'}`}>
                    {formatValue(fsA.roi_multiplier)}x
                    {winner === 'scenario_a' && <span className="ml-2 text-xs bg-yellow-400 text-yellow-900 px-1.5 py-0.5 rounded">WINNER</span>}
                  </td>
                  <td className={`px-4 py-3 text-center font-bold ${safeNum(fsB.roi_percent) >= 0 ? 'text-emerald-600' : 'text-rose-600'} ${winner === 'scenario_b' ? 'bg-purple-100' : 'bg-purple-50/50'}`}>
                    {formatValue(fsB.roi_multiplier)}x
                    {winner === 'scenario_b' && <span className="ml-2 text-xs bg-yellow-400 text-yellow-900 px-1.5 py-0.5 rounded">WINNER</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`font-bold ${safeNum(diff.roi_diff) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {(safeNum(fsA.roi_multiplier) - safeNum(fsB.roi_multiplier)) >= 0 ? '+' : ''}{(safeNum(fsA.roi_multiplier) - safeNum(fsB.roi_multiplier)).toFixed(1)}x
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium text-gray-900">Payback Period</td>
                  <td className="px-4 py-3 text-center text-blue-600 bg-blue-50/50">{formatValue(fsA.payback_months)} months</td>
                  <td className="px-4 py-3 text-center text-purple-600 bg-purple-50/50">{formatValue(fsB.payback_months)} months</td>
                  <td className="px-4 py-3 text-center">
                    <span className={safeNum(diff.payback_diff) <= 0 ? 'text-emerald-600' : 'text-rose-600'}>
                      {safeNum(diff.payback_diff) >= 0 ? '+' : ''}{formatValue(diff.payback_diff)} months
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium text-gray-900">KPI Improvement</td>
                  <td className="px-4 py-3 text-center text-blue-600 bg-blue-50/50">{formatValue(fsA.kpi_uplift_percent, 2)}%</td>
                  <td className="px-4 py-3 text-center text-purple-600 bg-purple-50/50">{formatValue(fsB.kpi_uplift_percent, 2)}%</td>
                  <td className="px-4 py-3 text-center">
                    <span className={(safeNum(fsB.kpi_uplift_percent) - safeNum(fsA.kpi_uplift_percent)) >= 0 ? 'text-emerald-600' : 'text-rose-600'}>
                      {formatDiff(safeNum(fsB.kpi_uplift_percent) - safeNum(fsA.kpi_uplift_percent), true)}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          {/* Intervention Details */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold">A</div>
                Scenario A Interventions
              </h4>
              <div className="space-y-2">
                {(scenario_a?.interventions || []).map((int, idx) => (
                  <div key={int.name || `int-a-${idx}`} className="bg-white p-2 rounded text-sm">
                    <span className="font-medium text-slate-900">{int.intervention_name}</span>
                    <span className="text-gray-500 ml-1">via {int.modality_name}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-purple-50 rounded-lg p-4">
              <h4 className="font-semibold text-purple-800 mb-3 flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-purple-600 text-white flex items-center justify-center text-sm font-bold">B</div>
                Scenario B Interventions
              </h4>
              <div className="space-y-2">
                {(scenario_b?.interventions || []).map((int, idx) => (
                  <div key={int.name || `int-b-${idx}`} className="bg-white p-2 rounded text-sm">
                    <span className="font-medium text-slate-900">{int.intervention_name}</span>
                    <span className="text-gray-500 ml-1">via {int.modality_name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Key Insights */}
          <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
            <h4 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
              <Info size={18} />
              Key Insights
            </h4>
            <ul className="space-y-1 text-sm text-amber-800">
              {safeNum(diff.investment_diff) > 0 && (
                <li>• Scenario B requires ${Math.abs(safeNum(diff.investment_diff)).toLocaleString()} more investment than Scenario A</li>
              )}
              {safeNum(diff.investment_diff) < 0 && (
                <li>• Scenario B saves ${Math.abs(safeNum(diff.investment_diff)).toLocaleString()} compared to Scenario A</li>
              )}
              {winner && winner !== 'tie' && (
                <li>• {winner === 'scenario_a' ? 'Scenario A' : 'Scenario B'} delivers {Math.abs(safeNum(fsA.roi_multiplier) - safeNum(fsB.roi_multiplier)).toFixed(1)}x better ROI</li>
              )}
              {safeNum(diff.payback_diff) < 0 && (
                <li>• Scenario B has a faster payback period by {Math.abs(safeNum(diff.payback_diff)).toFixed(1)} months</li>
              )}
              {safeNum(diff.payback_diff) > 0 && (
                <li>• Scenario A has a faster payback period by {Math.abs(safeNum(diff.payback_diff)).toFixed(1)} months</li>
              )}
            </ul>
          </div>
        </div>
        
        <div className="p-6 border-t bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700"
          >
            Close Comparison
          </button>
        </div>
      </div>
    </div>
  );
};
